import { useContext } from "react";
import { useParams } from "react-router";
import { DashboardUtils } from "../../dashboard_utils";
import { SeshPrimaryButton } from "../../forms/improved_components/base_components/sesh_primary_button";
import { trpc } from "../../utils/trpc";
import { GuildSettingsContext } from "../guild_settings";

export function AISettingsPage(props: {
	ai_toggler: JSX.Element;
	onPremiumClicked: () => void;
}) {
	let { server_id } = useParams<{
		server_id: string;
	}>();

	const existing_waitlist_query = trpc.waitlist.get.useQuery({
		guild_id: server_id,
	});

	const { is_premium, is_admin } = useContext(GuildSettingsContext);

	const context = trpc.useContext();

	const add_waitlist_mutation = trpc.waitlist.set.useMutation({
		onSuccess: (new_data) => {
			context.waitlist.get.setData(
				(data) => {
					return new_data;
				},
				{
					guild_id: server_id,
				}
			);

			DashboardUtils.ShowSuccessMessageAlert(
				"Added to Waitlist",
				<>
					Your server has been successfully added to the waitlist. You
					will be notified via DM when{" "}
					<span className="sesh-text">sesh</span> AI features are
					available for this server.
					<br />
					<br />
					You have also been granted access to the #ai-alpha channel
					in the sesh.fyi Discord server where you can give feedback
					and be the first to hear updates.
				</>
			);
		},
	});

	if (!existing_waitlist_query.isSuccess) {
		return <>{DashboardUtils.RenderLoadingThrobber()}</>;
	}

	let body_element: JSX.Element;

	// Server is still waiting on waitlist or not on waitlist yet
	if (!existing_waitlist_query.data?.is_active) {
		const is_on_waitlist = Boolean(existing_waitlist_query.data);

		let button_text: string;

		if (!is_premium) {
			button_text = "Must have Premium";
		} else {
			if (!is_admin) {
				button_text = "Must be Server Admin";
			} else {
				button_text = is_on_waitlist
					? "Server Already on Waitlist"
					: "Join the Waitlist";
			}
		}

		body_element = (
			<div className="flex flex-col items-center gap-2">
				<div className="note">
					<span className="sesh-text">sesh</span> AI is currently in
					alpha. You can join the waitlist now to get a sneak peek for
					your server. The waitlist is currently only available to
					servers that have{" "}
					<span
						className="manual-link"
						onClick={props.onPremiumClicked}
					>
						Premium
					</span>{" "}
					activated.
					<br />
					<br />
					We're adding new servers from the waitlist every few days so
					signup now!
				</div>
				<div className="">
					<SeshPrimaryButton
						className="w-fit px-4"
						disabled={is_on_waitlist || !is_premium || !is_admin}
						onClick={() =>
							add_waitlist_mutation.mutate({
								guild_id: server_id,
							})
						}
						is_loading={add_waitlist_mutation.isLoading}
					>
						<div className="flex items-center gap-3">
							<div className="text-lg">
								<i className="fas fa-clipboard-list fa-lg"></i>
							</div>
							<p>{button_text}</p>
						</div>
					</SeshPrimaryButton>
				</div>
			</div>
		);
	} else {
		body_element = props.ai_toggler;
	}

	return (
		<div className="flex flex-col gap-2">
			<div className="title is-3 has-text-centered mt-6 font-bold">
				<i className={`fa-lg title-icon fas fa-robot`}></i>
				AI Settings
				<div className="tag alpha-tag ml-2">
					<span className="">Alpha</span>
				</div>
			</div>
			{body_element}
		</div>
	);
}
